import React, { useEffect, useState, useCallback } from 'react';
import { View, Text, StyleSheet, FlatList, ActivityIndicator, TouchableOpacity, Modal, TextInput, ScrollView } from 'react-native';
import { Ionicons } from '@expo/vector-icons';

import { useLocalSearchParams } from 'expo-router';
import { api } from '../../src/api/client';
import { AdminToolbar } from '../../src/components/AdminList';
import ContentContainer from '../../src/components/ContentContainer';
import PageFooter from '../../src/components/PageFooter';
import InlineConfirm from '../../src/components/InlineConfirm';
import InlineMessage from '../../src/components/InlineMessage';
import { useTheme } from '../../src/context/ThemeContext';
import { useResponsive } from '../../src/utils/responsive';
import { spacing, fontSize, fontWeight, borderRadius } from '../../src/utils/theme';
import type { ColorTheme } from '../../src/utils/theme';
import { useModalBottomPadding } from '../../src/utils/useModalBottomPadding';
import { bundleItemsSummary } from '../../src/utils/bundleItems';

interface BundlePlanItem {
  readonly id: string;
  readonly name: string;
  description: string | null;
  tierName: string;
  washCount: number;
  items?: {
    kind: 'wash' | 'vac_minutes' | 'vending_credit';
    tierName: string | null;
    quantity: number | null;
    minutes: number | null;
    creditCents: number | null;
  }[];
  bundlePrice: number;
  locationScope: string;
  locationIds: string[] | null;
  expirationDays: number | null;
  expirationMinutes?: number | null;
  isActive: boolean;
  businessName: string | null;
  purchaserCount: number;
  createdAt: string;
}

const fmt = (cents: number) => `$${(cents / 100).toFixed(2)}`;

/** AdminBundlePlans - list, search, and manage wash bundle plans across all owners. */
export default function AdminBundlePlans() {
  const { ownerId: paramOwnerId } = useLocalSearchParams<{ ownerId?: string }>();
  const { colors } = useTheme();
  const { width, height } = useResponsive();
  const isMobile = width < 768;
  const styles = useStyles(colors, isMobile);
  const modalBottomPadding = useModalBottomPadding();
  const [plans, setPlans] = useState<BundlePlanItem[]>([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [page, setPage] = useState(1);
  const [total, setTotal] = useState(0);
  const [editItem, setEditItem] = useState<BundlePlanItem | null>(null);
  const [editData, setEditData] = useState<any>(null);
  const [saving, setSaving] = useState(false);
  const [confirmDelete, setConfirmDelete] = useState<string | null>(null);
  const [deleting, setDeleting] = useState(false);
  const [inlineMsg, setInlineMsg] = useState<{ type: 'success' | 'error'; message: string } | null>(null);

  const handleDelete = async () => {
    if (!confirmDelete) return;
    setDeleting(true);
    try {
      await api(`/api/admin/bundle-plans/${confirmDelete}`, { method: 'DELETE' });
      setConfirmDelete(null);
      setInlineMsg({ type: 'success', message: 'Bundle plan deleted' });
      fetchPlans(page, search);
    } catch (err: any) { setInlineMsg({ type: 'error', message: err.message }); }
    finally { setDeleting(false); }
  };

  const fetchPlans = useCallback(async (p = 1, s = '', signal?: AbortSignal) => {
    setLoading(true);
    try {
      const params = new URLSearchParams({ page: String(p), limit: '20' });
      if (s) params.set('search', s);
      if (paramOwnerId) params.set('ownerId', paramOwnerId);
      const data = await api<{ plans: BundlePlanItem[]; total: number }>(`/api/admin/bundle-plans?${params}`);
      if (signal?.aborted) return;
      setPlans(data.plans);
      setTotal(data.total);
      setPage(p);
    } catch (err) { if (!(signal?.aborted)) console.error(err); }
    finally { if (!(signal?.aborted)) setLoading(false); }
  }, [paramOwnerId]);

  useEffect(() => { const ac = new AbortController(); fetchPlans(1, '', ac.signal); return () => ac.abort(); }, [paramOwnerId]);

  const openEdit = (item: BundlePlanItem) => {
    setEditItem(item);
    setEditData({
      name: item.name,
      description: item.description || '',
      tierName: item.tierName,
      washCount: String(item.washCount),
      bundlePrice: String(item.bundlePrice),
      locationScope: item.locationScope,
      locationIds: item.locationIds || [],
      expirationDays: item.expirationDays != null ? String(item.expirationDays) : '',
      isActive: item.isActive,
    });
  };

  const handleSave = async () => {
    if (!editItem || !editData) return;
    setSaving(true);
    try {
      await api(`/api/admin/bundle-plans/${editItem.id}`, {
        method: 'PUT',
        body: {
          name: editData.name,
          description: editData.description || null,
          // Tier comes from the wash-tier catalog at creation; immutable here.
          washCount: parseInt(editData.washCount) || 1,
          bundlePrice: parseInt(editData.bundlePrice) || 0,
          locationScope: editData.locationScope,
          locationIds: editData.locationScope === 'selected' ? editData.locationIds : null,
          expirationDays: editData.expirationDays ? parseInt(editData.expirationDays) : null,
          isActive: editData.isActive,
        },
      });
      setEditItem(null);
      fetchPlans(page, search);
    } catch (err: any) { console.error(err); }
    finally { setSaving(false); }
  };

  const renderItem = ({ item }: { item: BundlePlanItem }) => (
    <View style={styles.row}>
      <View style={styles.rowBody}>
        <View style={styles.rowHeader}>
          <Text style={styles.name}>{item.name}</Text>
          <View style={[styles.badge, { backgroundColor: item.isActive ? '#05966915' : '#dc262615' }]}>
            <Text style={[styles.badgeText, { color: item.isActive ? '#059669' : '#dc2626' }]}>
              {item.isActive ? 'Active' : 'Inactive'}
            </Text>
          </View>
        </View>
        <Text style={styles.meta}>
          {item.items?.length
            ? `${bundleItemsSummary(item.items)} · ${fmt(item.bundlePrice)}`
            : `Tier: ${item.tierName} · ${item.washCount} washes · ${fmt(item.bundlePrice)}`}
          {' · '}{item.locationScope === 'all' ? 'All locations' : `${item.locationIds?.length || 0} location${(item.locationIds?.length || 0) !== 1 ? 's' : ''}`}
        </Text>
        <Text style={styles.subText}>
          Owner: {item.businessName || '—'} · {item.purchaserCount} active purchaser{item.purchaserCount !== 1 ? 's' : ''}
          {item.expirationMinutes
            ? ` · Same-visit (${item.expirationMinutes} min)`
            : item.expirationDays ? ` · Expires in ${item.expirationDays}d` : ' · No expiration'}
        </Text>
      </View>
      <TouchableOpacity onPress={() => openEdit(item)} hitSlop={8}>
        <Ionicons name="create-outline" size={18} color={colors.primary} />
      </TouchableOpacity>
      <TouchableOpacity onPress={() => setConfirmDelete(item.id)} hitSlop={8}>
        <Ionicons name="trash-outline" size={18} color={colors.danger} />
      </TouchableOpacity>
    </View>
  );

  return (
    <View style={styles.container}>
      <ContentContainer role="admin" footer={false}>
      <AdminToolbar
        search={search} onSearchChange={setSearch} onSearch={() => fetchPlans(1, search)}
        total={total} page={page} limit={20} onPageChange={(p) => fetchPlans(p, search)}
        csvEndpoint={`/api/admin/bundle-plans?${new URLSearchParams({ ...(search && { search }) })}`}
        csvFilename="bundle-plans.csv"
        label="bundle plan"
      />
      {loading ? (
        <ActivityIndicator size="large" color={colors.primary} style={{ marginTop: spacing.xl }} />
      ) : (
        <FlatList ListFooterComponent={<PageFooter />} data={plans} keyExtractor={item => item.id} renderItem={renderItem}
          ListEmptyComponent={<Text style={styles.empty}>No bundle plans found</Text>}
        />
      )}

      <InlineConfirm
        message={`Delete bundle plan "${plans.find(p => p.id === confirmDelete)?.name ?? ''}"?`}
        visible={!!confirmDelete}
        onConfirm={handleDelete}
        onCancel={() => setConfirmDelete(null)}
        confirmLabel="Delete"
        loading={deleting}
        variant="danger"
      />
      <InlineMessage type={inlineMsg?.type || 'error'} message={inlineMsg?.message || ''} visible={!!inlineMsg} onDismiss={() => setInlineMsg(null)} />

      <Modal visible={!!editItem} transparent animationType="slide" onRequestClose={() => setEditItem(null)}>
        <View style={styles.modalOverlay}>
          <View style={[styles.modalContent, { paddingBottom: modalBottomPadding }]}>
            <Text style={styles.modalTitle}>Edit Bundle Plan</Text>
            {editData && (
              <ScrollView style={{ maxHeight: isMobile ? height * 0.6 : 500 }} nestedScrollEnabled>
                <Text style={styles.fieldLabel}>Plan Name</Text>
                <TextInput style={styles.fieldInput} value={editData.name} onChangeText={(v) => setEditData({ ...editData, name: v })} maxLength={32} />
                <Text style={styles.fieldLabel}>Description</Text>
                <TextInput style={[styles.fieldInput, { height: 60, textAlignVertical: 'top' }]} value={editData.description} onChangeText={(v) => setEditData({ ...editData, description: v })} multiline maxLength={500} />
                <Text style={styles.fieldLabel}>{editItem?.items?.length ? 'Items' : 'Wash Tier'}</Text>
                <Text style={[styles.fieldInput, { color: colors.textSecondary }]}>
                  {editItem?.items?.length ? `${bundleItemsSummary(editItem.items)} (set at creation)` : `${editData.tierName || '—'} (set at creation)`}
                </Text>
                <Text style={styles.fieldLabel}>Wash Count</Text>
                <TextInput style={styles.fieldInput} value={editData.washCount} onChangeText={(v) => setEditData({ ...editData, washCount: v })} keyboardType="number-pad" maxLength={6} />
                <Text style={styles.fieldLabel}>Bundle Price (cents)</Text>
                <TextInput style={styles.fieldInput} value={editData.bundlePrice} onChangeText={(v) => setEditData({ ...editData, bundlePrice: v })} keyboardType="number-pad" maxLength={10} />
                <Text style={styles.fieldLabel}>Location Scope</Text>
                <View style={{ flexDirection: 'row', gap: spacing.sm, marginTop: 4, marginBottom: spacing.sm }}>
                  {['all', 'selected'].map(s => (
                    <TouchableOpacity key={s} onPress={() => setEditData({ ...editData, locationScope: s })}
                      style={[styles.scopeChip, editData.locationScope === s && styles.scopeChipActive]}>
                      <Text style={[styles.scopeChipText, editData.locationScope === s && { color: colors.white }]}>
                        {s === 'all' ? 'All Locations' : 'Selected'}
                      </Text>
                    </TouchableOpacity>
                  ))}
                </View>
                {editData.locationScope === 'selected' && (
                  <>
                    <Text style={styles.fieldLabel}>Location IDs (JSON array)</Text>
                    <TextInput style={styles.fieldInput} value={JSON.stringify(editData.locationIds)} onChangeText={(v) => { try { setEditData({ ...editData, locationIds: JSON.parse(v) }); } catch {} }} placeholder='["uuid1","uuid2"]' placeholderTextColor={colors.textLight} />
                  </>
                )}
                <Text style={styles.fieldLabel}>Expiration Days (blank = never)</Text>
                <TextInput style={styles.fieldInput} value={editData.expirationDays} onChangeText={(v) => setEditData({ ...editData, expirationDays: v })} keyboardType="number-pad" placeholder="No expiration" placeholderTextColor={colors.textLight} maxLength={5} />
                <TouchableOpacity style={styles.toggleRow} onPress={() => setEditData({ ...editData, isActive: !editData.isActive })}>
                  <Ionicons name={editData.isActive ? 'checkmark-circle' : 'close-circle'} size={24} color={editData.isActive ? '#059669' : '#dc2626'} />
                  <Text style={styles.toggleText}>{editData.isActive ? 'Active' : 'Inactive'}</Text>
                </TouchableOpacity>
              </ScrollView>
            )}
            <View style={styles.modalActions}>
              <TouchableOpacity style={styles.cancelBtn} onPress={() => setEditItem(null)}>
                <Text style={styles.cancelText}>Cancel</Text>
              </TouchableOpacity>
              <TouchableOpacity style={styles.saveBtn} onPress={handleSave} disabled={saving}>
                <Text style={styles.saveText}>{saving ? 'Saving...' : 'Save'}</Text>
              </TouchableOpacity>
            </View>
          </View>
        </View>
      </Modal>
      </ContentContainer>
    </View>
  );
}

const createStyles = (c: ColorTheme, isMobile: boolean) => StyleSheet.create({
  container: { flex: 1, backgroundColor: c.background, padding: spacing.md },
  row: { flexDirection: 'row', alignItems: 'center', paddingVertical: 10, borderBottomWidth: 1, borderBottomColor: c.borderLight, gap: 8 },
  rowBody: { flex: 1 },
  rowHeader: { flexDirection: 'row', justifyContent: 'space-between', alignItems: 'center' },
  name: { fontSize: fontSize.sm, fontWeight: fontWeight.semibold, color: c.text },
  badge: { paddingHorizontal: 8, paddingVertical: 2, borderRadius: 8 },
  badgeText: { fontSize: fontSize.xs, fontWeight: fontWeight.semibold },
  meta: { fontSize: fontSize.xs, color: c.textSecondary, marginTop: 2 },
  subText: { fontSize: fontSize.xs, color: c.textLight, marginTop: 2 },
  empty: { textAlign: 'center', color: c.textSecondary, marginTop: spacing.md, fontSize: fontSize.sm },
  modalOverlay: { flex: 1, backgroundColor: 'rgba(0,0,0,0.5)', justifyContent: isMobile ? 'flex-end' : 'center', alignItems: 'center', padding: isMobile ? 0 : spacing.lg },
  modalContent: { backgroundColor: c.surface, borderRadius: isMobile ? 0 : borderRadius.lg, borderTopLeftRadius: borderRadius.lg, borderTopRightRadius: borderRadius.lg, padding: spacing.lg, width: '100%', maxWidth: isMobile ? undefined : 420 },
  modalTitle: { fontSize: fontSize.lg, fontWeight: fontWeight.bold, color: c.text, marginBottom: spacing.md },
  fieldLabel: { fontSize: fontSize.xs, fontWeight: fontWeight.semibold, color: c.textSecondary, marginTop: spacing.sm, textTransform: 'uppercase' },
  fieldInput: { backgroundColor: c.surfaceSecondary, borderRadius: borderRadius.sm, padding: spacing.sm, fontSize: fontSize.sm, color: c.text, marginTop: 4, borderWidth: 1, borderColor: c.border },
  toggleRow: { flexDirection: 'row', alignItems: 'center', gap: spacing.sm, paddingVertical: spacing.sm },
  toggleText: { fontSize: fontSize.md, color: c.text },
  modalActions: { flexDirection: 'row', justifyContent: 'flex-end', gap: spacing.sm, marginTop: spacing.md },
  cancelBtn: { paddingHorizontal: spacing.md, paddingVertical: spacing.sm, borderRadius: borderRadius.sm },
  cancelText: { fontSize: fontSize.sm, color: c.textSecondary, fontWeight: fontWeight.medium },
  saveBtn: { paddingHorizontal: spacing.md, paddingVertical: spacing.sm, borderRadius: borderRadius.sm, backgroundColor: c.primary },
  saveText: { fontSize: fontSize.sm, color: c.white, fontWeight: fontWeight.semibold },
  scopeChip: { paddingHorizontal: 14, paddingVertical: 8, borderRadius: borderRadius.md, backgroundColor: c.surfaceSecondary, borderWidth: 1, borderColor: c.border },
  scopeChipActive: { backgroundColor: c.primary, borderColor: c.primary },
  scopeChipText: { fontSize: fontSize.sm, fontWeight: fontWeight.medium, color: c.textSecondary },
});

const useStyles = (c: ColorTheme, isMobile: boolean) => React.useMemo(() => createStyles(c, isMobile), [c, isMobile]);
